import React from 'react';
import MediaQuery from 'react-responsive';
import PropTypes from 'prop-types';
import uniq from 'lodash/uniq';
import {
  Badge,
  CaptionText,
  HeadingText,
  Link,
  Container,
  Row,
  Col,
  v2blue600,
  v2grey600,
  BodyText,
  Pagination,
  white,
  DropdownMenu,
} from '@paypalcorp/pp-react';
import { ThemeProvider } from '@paypalcorp/pp-react-theme';
import { GivingZoneTile } from '@paypalcorp/giving-fundraising';

import * as analytics from '../lib/analytics';
import { css } from 'glamor';
import { createHandler } from 'griffin-paypal/number';
import { DataContext } from '../lib/context';

require('es6-promise').polyfill();

const THUMB_IMAGE_IMAGE_ID = 4; // max 640 x 480

const spinerStyle = {
  textAlign: 'center',
  padding: '15px 0px 10px 0px',
  width: '200px',
  height: '60px',
  margin: 'auto',
};

const paginationStyle = css({
  transform: 'translateY(50%)',
  '@media(max-width: 768px)': {
    marginLeft: '4px',
    transform: 'translateY(25%)',
  },
});

const pageStyle = css({
  color: v2blue600,
  margin: '0 11px',
  ':hover': {
    color: v2blue600,
  },
  '@media(max-width: 500px)': {
    color: v2blue600,
    margin: '0 7px',
    ':hover': {
      color: v2blue600,
    },
  },
});

const activePageStyle = css({
  backgroundColor: v2blue600 + '!important',
  transition: 'none',
  ':hover': {
    color: white,
  },
});

const pageArrowSpaceStyle = css({
  width: '0',
});

const pageArrow = css({
  ':hover': {
    color: v2blue600,
  },
  '@media(max-width: 500px)': {
    margin: '0 0',
    padding: '4px 0px',
  },
});

const totalResultsTextStyle = {
  color: v2grey600,
};

export default class extends React.Component {
  static contextType = DataContext;

  static propTypes = {
    query: PropTypes.object,
    campaigns: PropTypes.array,
    totalItems: PropTypes.number,
    states: PropTypes.object,
    searchUrl: PropTypes.string,
    charityUrl: PropTypes.string,
    contentCountry: PropTypes.string,
    isMobile: PropTypes.bool,
  };

  state = {
    page: this.props.query.page ? Number(this.props.query.page) : 1,
    campaigns: this.props.campaigns || [],
    totalPages: Number(this.props.totalCampaignsPages),
    sortBy: this.props.query.sortBy ? this.props.query.sortBy : 'bestmatch',
    pageSize: this.props.query.pageSize
      ? Number(this.props.query.pageSize)
      : 24,
  };

  analyticsData = {
    e: 'cl',
    product: 'dw_giving',
    account_type: analytics.getAccountType(this.props.authState),
    event_source: analytics.getEventSource(this.props.isMobile),
    acct_cntry: this.props.contentCountry,
    filter_type: this.props.query.type ? this.props.query.type : '',
    state: this.props.query.state ? this.props.query.state : '',
    category_code: this.props.query.category ? this.props.query.category : '',
  };

  recordShowMoreEvent = () => {
    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_MORE_FUNDRAISERS_PRESSED,
      this.analyticsData
    );
  };

  recordSortPressedEvent = () => {
    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_SORT_DROPDOWN_PRESSED,
      this.analyticsData
    );
  };

  recordSortSelectionEvent = (optSel) => {
    let optionSelected = 'best_match';
    if (optSel === 'name') {
      optionSelected = 'alphabetical';
    }

    if (optSel === 'trending') {
      optionSelected = 'popular';
    }

    if (optSel === 'bestmatch') {
      optionSelected = 'best_match';
    }

    if (optSel === 'endingFirst') {
      optionSelected = 'ending_first';
    }

    if (optSel === 'closestToGoal') {
      optionSelected = 'closest_to_goal';
    }

    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_SORT_SELECTION_PRESSED,
      Object.assign({}, this.analyticsData, {
        opt_sel: optionSelected,
      })
    );
  };

  recordPageSizePressedEvent = () => {
    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_PAGE_SIZE_DROPDOWN_PRESSED,
      this.analyticsData
    );
  };

  recordPageSizeSelectionEvent = (optSel) => {
    analytics.instance.recordEvent(
      analytics.events
        .DW_GIVING_BROWSE_FUNDRAISER_CHARITY_PAGE_SIZE_SELECTION_PRESSED,
      Object.assign({}, this.analyticsData, {
        opt_sel: optSel,
      })
    );
  };

  recordPaginationPressedEvent = (optSel) => {
    this.handleLoad(optSel);
    analytics.instance.recordEvent(
      analytics.events.DW_GIVING_BROWSE_FUNDRAISER_CHARITY_PAGINATION_PRESSED,
      Object.assign({}, this.analyticsData, {
        opt_sel: optSel,
      })
    );
  };

  handleLoad = (nextPage) => {
    const urlParams = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );

    const paramsObj = Object.assign({}, urlParams, {
      page: nextPage,
      pageSize: this.state.pageSize,
      sortBy: this.state.sortBy,
    });

    const params = new URLSearchParams(paramsObj);

    const url = new URL(window.location.origin);
    url.pathname = window.location.pathname;
    url.search = params;
    window.location = url;
  };

  handlePageSize = (e) => {
    this.recordPageSizeSelectionEvent(e.target.value);
    this.setState(
      {
        pageSize: e.target.value,
      },
      () => {
        this.handleLoad(1);
      }
    );
  };

  handleSortBy = (e) => {
    this.recordSortSelectionEvent(e.target.value);
    this.setState(
      {
        sortBy: e.target.value,
      },
      () => {
        this.handleLoad(1);
      }
    );
  };

  renderItemRow = ({ campaign, index, cardContent } = {}) => {
    let dataFPTI;
    try {
      dataFPTI = JSON.parse(cardContent.instrumentationData);
    } catch (e) {
      dataFPTI = {};
    }

    dataFPTI.account_type =
      this.props.authState && this.props.authState === 'ANONYMOUS'
        ? 'guest'
        : 'member';

    return (
      <Col xs={12} md={6} lg={4} className='itemCampaign'>
        <div
          onClick={(event) =>
            analytics.onTileClick(event, {
              contentCountry: this.props.contentCountry,
              authState: analytics.getAccountType(this.props.authState),
              order: index,
            })
          }
          data-testid={`fundraiserTile-${index}`}
        >
          <GivingZoneTile
            type='fundraiser'
            domain='/'
            isLoading={false}
            openNewTab={this.props.searchType !== 'npoFundraisers'}
            cardContent={cardContent}
            key={`item-${campaign.id}-${index}`}
            data-fpti={JSON.stringify(dataFPTI)}
          />
        </div>
      </Col>
    );
  };

  getCampaignListThumbnailImage(imageUrl, categories) {
    if (!imageUrl || imageUrl === '') {
      if (categories.length > 0) {
        switch (categories[0]) {
          case '1001':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/arts-culture.svg';
          case '1002':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/education.svg';
          case '1003':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/enviroment.svg';
          case '1004':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/animal-wildlife.svg';
          case '1005':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/health-medicine.svg';
          case '1022':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/social-justice.svg';
          case '1007':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/business-employment.svg';
          case '1008':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/housing-hunger.svg';
          case '1009':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/disaster-relief.svg';
          case '1010':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/sports-recreation.svg';
          case '1011':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/children-youth.svg';
          case '1012':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/human-services.svg';
          case '1013':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/international.svg';
          case '1014':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/community.svg';
          case '1015':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/philanthropy-grants.svg';
          case '1016':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/science-research.svg';
          case '1017':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/military-veterans.svg';
          case '1018':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/faith-spirituality.svg';
          case '1019':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/general-support.svg';
          case '1020':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/family-support.svg';
          case '1021':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/memorial-expenses.svg';
          case '1006':
            return 'https://www.paypalobjects.com/paypal-ui/illustrations/focused/svg/crime-justice.svg';
          default:
            break;
        }
      } else {
        return ''; // Specific case scenario where a fundraiser doesn't have a background image configured and neither a category
      }
    } else if (imageUrl.includes('file.')) {
      // backwards compatability for static images
      return imageUrl;
    }
    return imageUrl.replace('image_0', `image_${THUMB_IMAGE_IMAGE_ID}`);
  }

  renderList = (campaigns) => {
    const sizePageOptions = [
      { primaryText: '24', value: 24 },
      { primaryText: '48', value: 48 },
      { primaryText: '94', value: 94 },
    ];

    const sortByOptions = [
      {
        primaryText: this.props.i18n('label.sort-by-best-match'),
        value: 'bestmatch',
      },
      {
        primaryText: this.props.i18n('label.sort-by-alphabetical'),
        value: 'name',
      },
      {
        primaryText: this.props.i18n('label.sort-by-popular'),
        value: 'trending',
      },
      {
        primaryText: this.props.i18n('label.sort-by-ending-first'),
        value: 'endingFirst',
      },
      {
        primaryText: this.props.i18n('label.sort-by-closest-to-goal'),
        value: 'closestToGoal',
      },
    ];

    const items = campaigns
      .filter((campaign) => !!campaign)
      .map((campaign, index) => {
        const {
          template_data = '{"background_image":""}',
          participants = [],
          transaction_summary,
          categories = [],
        } = campaign;
        const templateDataObj = JSON.parse(template_data);
        let charityName = '';
        if (participants && participants.length > 0) {
          charityName = participants[0].charity_info
            ? participants[0].charity_info.name
            : '';
        }
        const imageUrl = this.getCampaignListThumbnailImage(
          templateDataObj.background_image,
          categories
        );
        const cardContent = {
          id: campaign.id,
          actions: `[{"params":"campaignId=${campaign.id}"}]`,
          campaignImage: imageUrl,
          charityName: charityName,
          participantText: `${this.props
            .i18n('label.supporting')
            .replace('{fundraiserName}', '')} ${charityName}`,
          primaryImageUrl: imageUrl,
          campaignTitle: campaign.event_title,

          rangeEndText: `${this.props
            .i18n('label.of')
            .replace('{currentAmount}', '')
            .replace(' ', '')
            .replace('{targetAmount}', '')} ${campaign.formatedTargetAmount}`,
          rangeStartText: `${campaign.formatedCurrentAmount}`,
          totalAmountText: `${campaign.target_amount}`,
          contributedAmountText: `${transaction_summary.total_amount}`,
          instrumentationData: JSON.stringify([
            {
              item_name: 'dw_giving',
              card_type: 'fundraiser',
              tile_id: campaign.id,
              module: 'fundraiser',
              scope: 'tile',
              link_name: 'donate',
              domain: 'Cause',
              tile_id_type: 'campaign_id',
            },
          ]),
        };

        return (
          <>
            {this.renderItemRow({
              campaign,
              index,
              cardContent,
            })}
          </>
        );
      });

    let totalResults;
    if (this.props.searchType !== 'npoFundraisers') {
      const totalResultsText =
        campaigns.length === 1
          ? this.props.i18n('label.one-result')
          : this.props
              .i18n('label.total-fundraiser-results')
              .replace(
                '{totalResults}',
                this.props.totalCampaignsItems > this.state.maxResults
                  ? this.griffinNumberHandler.formatNumber(
                      this.state.maxResults
                    )
                  : this.griffinNumberHandler.formatNumber(
                      this.props.totalCampaignsItems
                    )
              );
      totalResults = (
        <div className={'total-results-container'}>
          <BodyText style={totalResultsTextStyle}>{totalResultsText}</BodyText>
        </div>
      );
    }

    let sortBy;
    if (this.props.i18n('label.sort-by').length > 1) {
      sortBy = (
        <ThemeProvider theme='v2'>
          <div className={'sort-by-container'}>
            {totalResults}
            {this.props.searchType === 'fundraisers' && (
              <div className={'select-sort-by'}>
                <DropdownMenu
                  label={this.props.i18n('label.sort-by')}
                  options={sortByOptions}
                  value={this.state.sortBy}
                  onClick={this.recordSortPressedEvent}
                  onChange={this.handleSortBy}
                />
              </div>
            )}
          </div>
        </ThemeProvider>
      );
    }

    const selectPageSize = (
      <ThemeProvider theme='v2'>
        <div className={'select-page-size'}>
          <DropdownMenu
            label={this.props.i18n('label.show')}
            options={sizePageOptions}
            value={this.state.pageSize}
            onClick={this.recordPageSizePressedEvent}
            onChange={this.handlePageSize}
          />
        </div>
      </ThemeProvider>
    );

    let pagination;
    if (this.state.totalPages > 1) {
      const totalPageCount =
        this.props.totalItems > this.state.maxResults
          ? Math.trunc(this.state.maxResults / this.state.pageSize)
          : this.state.totalPages;
      pagination = (
        <>
          <MediaQuery minWidth={769}>
            <div className='pagination-container'>
              <Pagination
                totalPageCount={totalPageCount}
                currentPage={this.state.page}
                onChange={this.recordPaginationPressedEvent}
                className={`${paginationStyle}`}
                activePageClassName={`${activePageStyle}`}
                pageClassName={`${pageStyle}`}
                arrowSpaceClassName={`${pageArrowSpaceStyle}`}
                arrowLeftClassName={`${pageArrow}`}
                arrowRightClassName={`${pageArrow}`}
              />
              {selectPageSize}
            </div>
          </MediaQuery>
          <MediaQuery maxWidth={768}>
            <div className='pagination-container'>
              <Pagination
                totalPageCount={totalPageCount}
                currentPage={this.state.page}
                onChange={this.recordPaginationPressedEvent}
                className={`${paginationStyle}`}
                activePageClassName={`${activePageStyle}`}
                pageClassName={`${pageStyle}`}
                arrowSpaceClassName={`${pageArrowSpaceStyle}`}
                arrowLeftClassName={`${pageArrow}`}
                arrowRightClassName={`${pageArrow}`}
              />
            </div>
          </MediaQuery>
        </>
      );
    }

    const urlParams = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );

    const paramsObj = Object.assign({}, urlParams, {
      type: 'fundraisers',
      page: 1,
      pageSize: 24,
      sortBy: 'bestmatch',
    });

    const params = new URLSearchParams(paramsObj);

    const url = new URL(window.location.origin);
    url.pathname = window.location.pathname;
    url.search = params;

    const showMore = (
      <Link target={''} href={url} onClick={this.recordShowMoreEvent}>
        {this.props.i18n('label.show-more-fundraisers')}
      </Link>
    );

    return (
      <>
        {sortBy}
        <Row>
          {this.props.searchType === 'fundraisers' ? items : items.slice(0, 9)}
        </Row>
        {this.props.searchType === 'fundraisers' && pagination}
        {items.length > 5 && this.props.searchType === 'all' && showMore}
      </>
    );
  };

  render() {
    if (!this.griffinNumberHandler) {
      this.griffinNumberHandler = createHandler(this.context.griffin.number);
    }

    return (
      <div className={'list'}>{this.renderList(this.state.campaigns)}</div>
    );
  }
}
